import React, { useCallback, useMemo } from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useQueryCountriesDetails } from '@/modules/shared/query-client/useQueryCountriesDetails'
import { useMutationCountriesDetails } from '@/modules/shared/query-client/useMutationCountriesDetails'
import { changeModified } from './utils'
import { ContentHeaderBar } from '../../components/ContentHeaderBar'
import {
  faFlag,
  faStar as faStarFilled,
} from '@fortawesome/free-solid-svg-icons'

import styles from './styles.module.scss'
import clx from 'classnames'
import { SettingsPageWrapper } from '../../components/SettingsPageWrapper'
import { CountryFlag } from '@/modules/shared/components/CountryFlag'
import { CsvPillsInput } from './CsvPillsInput'
import { EtBrandSygnet } from '@/modules/shared/components/svg/EtBrandSygnet'
import { RouteComponentProps } from '@/app/navigation/withRouter'

export const CountryPage = ({
  match,
}: RouteComponentProps<{ companyId: string }>) => {
  const { t } = useLocalize()
  const query = useQueryCountriesDetails({ companyId: match.params.companyId })
  const mutation = useMutationCountriesDetails(match.params.companyId)

  const [modifiedOftenUsedById, setModifiedOftenUsedById] = React.useState<
    Record<number, boolean>
  >({})
  const [modifiedAliasById, setModifiedAliasById] = React.useState<
    Record<number, string>
  >({})

  const isDirty =
    Object.keys(modifiedOftenUsedById).length +
      Object.keys(modifiedAliasById).length >
    0

  const handleReset = () => {
    setModifiedOftenUsedById({})
    setModifiedAliasById({})
  }

  const handleSave = () => {
    const prepared = structuredClone(saved).map((row) => {
      const { id } = row
      const checkedIsDirty = id in modifiedOftenUsedById
      const checked = checkedIsDirty ? modifiedOftenUsedById[id] : row.oftenUsed
      const aliasIsDirty = id in modifiedAliasById
      const alias = aliasIsDirty ? modifiedAliasById[id] : row.alias
      return {
        ...row,
        oftenUsed: checked,
        alias,
      }
    })
    mutation.mutateAsync(prepared)
  }

  const saved = useMemo(() => {
    return query.isFetched && Boolean(query.data) && Array.isArray(query.data)
      ? query.data
      : []
  }, [query.data, query.isFetched])

  const change = useCallback(
    (name: 'alias' | 'oftenUsed', id: number, value: string | boolean) => {
      const countryData = saved.find((c) => c.id === id)
      if (!countryData) {
        return
      }
      switch (name) {
        case 'alias':
          const savedAlias = countryData.alias
          changeModified<string>(
            setModifiedAliasById,
            id,
            String(value),
            savedAlias,
          )
          break
        case 'oftenUsed':
          const savedOftenUsed = countryData.oftenUsed
          changeModified<boolean>(
            setModifiedOftenUsedById,
            id,
            Boolean(value),
            savedOftenUsed,
          )
          break
      }
    },
    [saved],
  )

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value, checked, dataset, type } = e.target
      const { id } = dataset
      const _id = Number(id)
      if (!_id || isNaN(_id)) {
        return
      }
      change(
        name as 'alias' | 'oftenUsed',
        _id,
        type === 'checkbox' ? Boolean(checked) : value,
      )
    },
    [change],
  )

  return (
    <SettingsPageWrapper
      isReady
      isDirty={isDirty}
      isPending={query.isFetching || mutation.isPending}
      header={
        <>
          <ContentHeaderBar
            title={t('configuration-menu.countries').toString()}
            titleIcon={faFlag}
          />
        </>
      }
      handleSave={handleSave}
      handleReset={handleReset}
    >
      <section
        data-test-id="settings-countries-page"
        className={clx(styles.tableWrapper)}
      >
        <table className="tw-w-full tw-table-auto">
          <thead>
            <tr>
              <th>Domyślne</th>
              <th>Państwo</th>
              <th>Symbol</th>
              <th>Alias</th>
              <th>Waluta</th>
            </tr>
          </thead>
          <tbody>
            {saved.map((c) => {
              const { id } = c
              const checkedIsDirty = id in modifiedOftenUsedById
              const checked = checkedIsDirty
                ? modifiedOftenUsedById[id]
                : c.oftenUsed
              const aliasIsDirty = id in modifiedAliasById
              const alias = aliasIsDirty ? modifiedAliasById[id] : c.alias

              return (
                <tr key={c.id}>
                  <td>
                    <input
                      id={`oftenUsed-${id}`}
                      className="tw-hidden"
                      type="checkbox"
                      name="oftenUsed"
                      checked={checked}
                      onChange={handleChange}
                      data-id={id}
                    />

                    <label
                      htmlFor={`oftenUsed-${id}`}
                      className="tw-cursor-pointer"
                    >
                      <EtBrandSygnet 
                        monochrome={!checked}
                        width={20}
                        height={16}
                        stroke={checkedIsDirty ? 'currentColor' : ''}
                        className={clx('tw-h-6 tw-w-6', checkedIsDirty ? 'tw-text-red-400' : '')}
                      />
                    </label>
                  </td>
                  <td>
                    <div className="tw-flex tw-items-center tw-justify-start tw-gap-1">
                      <CountryFlag code={c.code} />
                      {c.name}
                    </div>
                  </td>
                  <td>{c.code}</td>
                  <td>
                    <CsvPillsInput
                      value={typeof alias === 'string' ? alias : ''}
                      setValue={(a) => change('alias', c.id, a)}
                      className={aliasIsDirty ? 'tw-text-red-400' : ''}
                    />
                    {/* <input
                        value={alias}
                        onChange={handleChange}
                        data-id={id}
                        name="alias"
                        type="text"
                        className={aliasIsDirty ? 'tw-text-red-400' : ''}
                      /> */}
                  </td>
                  <td>{c.currency.code}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </section>
    </SettingsPageWrapper>
  )
}
